/**
 * رپر Telegram Mini App.
 * دو قانون:
 *   ۱. اپ باید بیرون از تلگرام هم کار کند (نسخهٔ وب) — هیچ‌جا فرض نکنید تلگرام هست.
 *   ۲. initData هرگز سمت کلاینت اعتبارسنجی نمی‌شود. فقط خام به سرور فرستاده می‌شود.
 *      اگر این را رد کنید، هر کسی می‌تواند خودش را کاربر دیگری جا بزند.
 */

interface TgUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: { user?: TgUser; start_param?: string };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportStableHeight: number;
  ready(): void;
  expand(): void;
  close(): void;
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  setHeaderColor?(c: string): void;
  setBackgroundColor?(c: string): void;
  onEvent(e: string, cb: () => void): void;
  offEvent(e: string, cb: () => void): void;
  openLink(url: string, opts?: { try_instant_view?: boolean }): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
    selectionChanged(): void;
  };
  MainButton: {
    text: string;
    isVisible: boolean;
    show(): void;
    hide(): void;
    setText(t: string): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    showProgress(leave?: boolean): void;
    hideProgress(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

const wa = (): TgWebApp | undefined =>
  typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

/** آیا واقعاً داخل تلگرام باز شده‌ایم؟ وجود شیء کافی نیست — initData باید پر باشد. */
export const isTelegram = (): boolean => {
  const w = wa();
  return !!w && typeof w.initData === "string" && w.initData.length > 0;
};

/** فقط برای نمایش. هرگز به‌عنوان احراز هویت استفاده نشود. */
export const telegramUser = (): TgUser | undefined => wa()?.initDataUnsafe?.user;

/** رشتهٔ خامی که باید سمت سرور با HMAC توکن ربات اعتبارسنجی شود */
export const rawInitData = (): string => wa()?.initData ?? "";

/** پارامتر شروع (?startapp=) — برای کد دعوت یا لینک عمیق */
export const startParam = (): string | undefined => wa()?.initDataUnsafe?.start_param;

export function initTelegram(): void {
  const w = wa();
  if (!w) return;
  w.ready();
  w.expand();
  // بدون این، کشیدن صفحه به پایین وسط جلسهٔ مطالعه مینی‌اپ را می‌بندد
  w.disableVerticalSwipes?.();
  w.setHeaderColor?.("#211B26");
  w.setBackgroundColor?.("#FAF6EE");
}

/** موقع جلسهٔ تمرکز روشن می‌شود تا بستن تصادفی جلسه را از بین نبرد */
export function setClosingConfirmation(on: boolean): void {
  const w = wa();
  if (!w) return;
  if (on) w.enableClosingConfirmation?.();
}

type HapticKind = "tap" | "success" | "warning" | "error" | "select";
export function haptic(kind: HapticKind = "tap"): void {
  const h = wa()?.HapticFeedback;
  if (!h) return;
  try {
    if (kind === "tap") h.impactOccurred("light");
    else if (kind === "select") h.selectionChanged();
    else h.notificationOccurred(kind);
  } catch { /* روی نسخه‌های قدیمی تلگرام موجود نیست */ }
}

export function openExternal(url: string): void {
  const w = wa();
  if (w) w.openLink(url);
  else window.open(url, "_blank", "noopener");
}

/** دکمهٔ اصلی تلگرام — بیرون از تلگرام بی‌اثر است */
export const mainButton = {
  show(text: string, onClick: () => void): () => void {
    const w = wa();
    if (!w) return () => {};
    w.MainButton.setText(text);
    w.MainButton.onClick(onClick);
    w.MainButton.show();
    return () => {
      w.MainButton.offClick(onClick);
      w.MainButton.hide();
    };
  },
  hide(): void {
    wa()?.MainButton.hide();
  },
};

export const backButton = {
  show(onClick: () => void): () => void {
    const w = wa();
    if (!w) return () => {};
    w.BackButton.onClick(onClick);
    w.BackButton.show();
    return () => {
      w.BackButton.offClick(onClick);
      w.BackButton.hide();
    };
  },
};

export function platformName(): string {
  return wa()?.platform ?? "web";
}
